import { useEffect, useCallback, useRef, useMemo, useState } from "react";
import { connect } from "react-redux";
import { ResourceForm } from "components/ResourceForm/ResourceForm";
import {
	saveKeyPersonnelChangesForRfx,
	getPastKeyPersonnelForRfx,
} from "actions/RFQ/Supplier/rfqActions";
import { Box, Flex } from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SmartFieldCancelModal } from "./_components/SmartFieldCancelModal";
import { useSmartFieldEditor } from "../SmartFieldEditor/hooks/useSmartFieldEditor";
import {
	LoadingView,
	ErrorView,
	NoDataView,
} from "../SmartFieldEditor/_components/SmartFieldStatusViews";
import {
	processResumeFiles,
	processResumeFilesForSubmission,
} from "utils/resumeHelpers";
import { Button } from "newui/components/Button";

export const SmartFieldKeyPersonnelEditorComponent = ({
	keyPersonnelId,
	rfxId,
	companyId,
	onRequestClose,
	...props
}) => {
	const [isSaving, setIsSaving] = useState(false);
	const {
		isModalOpen,
		isModalSaving,
		setIsModalSaving,
		isInModalFlow,
		setIsInModalFlow,
		errorStatus,
		setErrorStatus,
		autoSaveDisabled,
		setAutoSaveDisabled,
		handleCancelClick,
		handleModalClose,
	} = useSmartFieldEditor({
		id: keyPersonnelId,
		rfxId,
		companyId,
		entityType: "KeyPersonnel",
	});

	const formikRef = useRef(null);

	// Store the current values for direct API calls
	const currentValuesRef = useRef(null);

	const queryClient = useQueryClient();

	const {
		data: keyPersonnelData,
		isLoading,
		error: keyPersonnelError,
	} = useQuery({
		queryKey: ["pastKeyPersonnelForRfx", { keyPersonnelId, rfqId: rfxId }],
		queryFn: getPastKeyPersonnelForRfx,
		enabled: !!keyPersonnelId && !!rfxId,
	});

	const initialValues = useMemo(() => {
		if (!keyPersonnelData) return null;

		// Handle projects_completed - could be array or object
		let projectsData = [];
		if (keyPersonnelData.projects_completed) {
			if (Array.isArray(keyPersonnelData.projects_completed)) {
				// Direct array case
				projectsData = keyPersonnelData.projects_completed;
			} else if (keyPersonnelData.projects_completed.data) {
				// {data: [...]} format case
				projectsData = keyPersonnelData.projects_completed.data;
			}
		}

		const {
			resumeFiles,
			resumeFilesIsDefault,
			resumeFilesId,
			resumeFilesIsRemove,
		} = processResumeFiles(keyPersonnelData.resume);

		return {
			first_name: keyPersonnelData.first_name || "",
			last_name: keyPersonnelData.last_name || "",
			email: keyPersonnelData.email || "",
			position: keyPersonnelData.position || "",
			experience: keyPersonnelData.experience?.toString() || "1",
			sector_ids: Array.isArray(keyPersonnelData.sectors)
				? keyPersonnelData.sectors.map((sector) => sector.id)
				: [],
			expertise_id: keyPersonnelData.expertise?.id || "",
			discipline_ids: Array.isArray(keyPersonnelData.disciplines)
				? keyPersonnelData.disciplines.map((discipline) => discipline.id)
				: [],
			projects_completed: projectsData,
			resume: keyPersonnelData.resume || [], // Keep original for backward compatibility
			resume_files: resumeFiles,
			resume_files_is_default: resumeFilesIsDefault,
			resume_files_id: resumeFilesId,
			resume_files_is_remove: resumeFilesIsRemove,
			resume_file: null,
			avatar_image: keyPersonnelData.avatar_location || "",
			company_id: keyPersonnelData.company_id || companyId,
			status: "published",
			roles: "default",
		};
	}, [keyPersonnelData, companyId]);

	// Update currentValuesRef when initialValues change
	useEffect(() => {
		if (initialValues) {
			currentValuesRef.current = initialValues;
		}
	}, [initialValues]);

	const invalidateKeyPersonnelQuery = useCallback(() => {
		queryClient.invalidateQueries([
			"pastKeyPersonnelForRfx",
			{ keyPersonnelId, rfqId: rfxId },
		]);
	}, [queryClient, keyPersonnelId, rfxId]);

	const makeDirectApiCall = useCallback(
		(values) => {
			setIsSaving(true);

			// Process resume_files data for API submission using helper
			if (values.resume_files && values.resume_files.length > 0) {
				const processedValues = processResumeFilesForSubmission(values);

				return saveKeyPersonnelChangesForRfx(
					keyPersonnelId,
					rfxId,
					processedValues
				)
					.then((result) => {
						invalidateKeyPersonnelQuery();
						queryClient.invalidateQueries({ queryKey: ["keyPersonnel"] });
						return result;
					})
					.catch((error) => {
						throw error;
					})
					.finally(() => {
						setIsSaving(false);
						setIsModalSaving(false);
					});
			}

			// If no resume files, just send the original values
			return saveKeyPersonnelChangesForRfx(keyPersonnelId, rfxId, values)
				.then((result) => {
					invalidateKeyPersonnelQuery();
					queryClient.invalidateQueries({ queryKey: ["keyPersonnel"] });
					return result;
				})
				.catch((error) => {
					throw error;
				})
				.finally(() => {
					setIsSaving(false);
					setIsModalSaving(false);
				});
		},
		[
			keyPersonnelId,
			rfxId,
			setIsSaving,
			setIsModalSaving,
			invalidateKeyPersonnelQuery,
			queryClient,
		]
	);

	const handleSaveButtonClick = useCallback(() => {
		setIsSaving(true);

		// get current form values, fall back to initial values if not available
		const values = currentValuesRef.current || initialValues;
		makeDirectApiCall(values)
			.then(() => {
				if (onRequestClose) onRequestClose();
			})
			.catch(() => {
				setIsSaving(false);
			});
	}, [makeDirectApiCall, initialValues, setIsSaving, onRequestClose]);

	const handleSaveFromModal = useCallback(() => {
		setIsModalSaving(true);

		const values = currentValuesRef.current || initialValues;
		makeDirectApiCall(values)
			.then(() => {
				if (onRequestClose) onRequestClose();
			})
			.catch(() => {
				setIsModalSaving(false);
			})
			.finally(() => {
				setIsInModalFlow(false);
			});
	}, [
		makeDirectApiCall,
		initialValues,
		setIsModalSaving,
		setIsInModalFlow,
		onRequestClose,
	]);

	const handleSubmit = useCallback(
		async (values, { setSubmitting, setErrors, setStatus }) => {
			// update current form values
			currentValuesRef.current = values;

			// only execute API call if not in modal flow or explicitly saving
			if (!isInModalFlow || isModalSaving) {
				try {
					await makeDirectApiCall(values);
				} catch (error) {
					if (error.errors) {
						setErrors(error.errors);
					}

					setStatus("Error");
					setErrorStatus(true);
				}
			}
			// reset modal flow flag
			if (!isModalOpen) {
				setIsInModalFlow(false);
			}

			setSubmitting(false);
		},
		[
			makeDirectApiCall,
			isInModalFlow,
			isModalSaving,
			isModalOpen,
			setErrorStatus,
			setIsInModalFlow,
		]
	);

	if (isLoading) {
		return <LoadingView entityType="key personnel" />;
	}

	if (keyPersonnelError) {
		return (
			<ErrorView
				error={keyPersonnelError}
				entityType="key personnel"
			/>
		);
	}

	if (!initialValues) {
		return <NoDataView entityType="key personnel" />;
	}

	return (
		<Box p={4}>
			<ResourceForm
				initialValues={initialValues}
				handleSubmit={handleSubmit}
				isRequesting={isSaving || isModalSaving}
				editForm={true}
				autoUpdateForm={autoSaveDisabled}
				setAutoUpdateForm={setAutoSaveDisabled}
				errorStatus={errorStatus}
				setErrorStatus={setErrorStatus}
				supplierSmartField={true}
				hideDefaultButtons={true}
				innerRef={(formik) => {
					formikRef.current = formik;
					// When formik is available, update our current values reference
					if (formik) {
						currentValuesRef.current = formik.values;
					}
				}}
				customButtons={
					<Flex
						justifyContent="flex-end"
						gap={4}
						mt={6}
					>
						<Button
							btntype="link"
							disabled={isSaving}
							onClick={handleCancelClick}
						>
							Cancel
						</Button>
						<Button
							type="button"
							isLoading={isSaving}
							disabled={isSaving}
							onClick={handleSaveButtonClick}
						>
							Save Changes to RFX
						</Button>
					</Flex>
				}
			/>
			<SmartFieldCancelModal
				isOpen={isModalOpen}
				onClose={handleModalClose}
				onDiscard={() => onRequestClose()}
				onSave={handleSaveFromModal}
				isSaving={isModalSaving}
			/>
		</Box>
	);
};

const mapStateToProps = (state) => {
	return {
		companyId: state.search.activeCompany.id,
	};
};

export const SmartFieldKeyPersonnelEditor = connect(mapStateToProps)(
	SmartFieldKeyPersonnelEditorComponent
);

export default SmartFieldKeyPersonnelEditor;
